/**
 * Scroll Performance Optimizations
 * Centralized utilities for optimizing scroll performance across the application
 */

import { useCallback, useEffect, useRef, useState } from "react";

// Throttle function for scroll events
export const throttle = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastExecTime = 0;

  return function (this: any, ...args: any[]) {
    const currentTime = Date.now();

    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(
        () => {
          func.apply(this, args);
          lastExecTime = Date.now();
        },
        delay - (currentTime - lastExecTime),
      );
    }
  };
};

// Debounce function for scroll events
export const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout | null = null;

  return function (this: any, ...args: any[]) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

// Optimized scroll handler hook with throttling
export const useOptimizedScrollHandler = (
  callback: () => void,
  dependencies: any[] = [],
  options: {
    throttleMs?: number;
    passive?: boolean;
    capture?: boolean;
  } = {},
) => {
  const { throttleMs = 16, passive = true, capture = false } = options;
  const callbackRef = useRef(callback);
  const throttledCallback = useRef<Function>();

  // Update callback ref when dependencies change
  useEffect(() => {
    callbackRef.current = callback;
  }, dependencies);

  // Create throttled callback
  useEffect(() => {
    throttledCallback.current = throttle(() => {
      callbackRef.current();
    }, throttleMs);
  }, [throttleMs]);

  useEffect(() => {
    const handleScroll = () => {
      if (throttledCallback.current) {
        throttledCallback.current();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive, capture });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [passive, capture]);
};

// Optimized intersection observer hook
export const useOptimizedIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: IntersectionObserverInit = {},
) => {
  const [elements, setElements] = useState<Element[]>([]);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (elements.length === 0) return;

    observer.current = new IntersectionObserver(callback, {
      rootMargin: "50px",
      threshold: 0.1,
      ...options,
    });

    elements.forEach((element) => {
      if (observer.current) {
        observer.current.observe(element);
      }
    });

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [elements, callback, options]);

  const addElement = useCallback(
    (element: Element | null) => {
      if (element && !elements.includes(element)) {
        setElements((prev) => [...prev, element]);
      }
    },
    [elements],
  );

  const removeElement = useCallback((element: Element) => {
    setElements((prev) => prev.filter((el) => el !== element));
  }, []);

  return { addElement, removeElement };
};

// Optimized section tracking for navigation
export const useOptimizedSectionTracking = (
  sectionIds: string[],
  onActiveChange: (activeSection: string) => void,
  options: {
    offset?: number;
    throttleMs?: number;
  } = {},
) => {
  const { offset = 200, throttleMs = 100 } = options;
  const [activeSection, setActiveSection] = useState("");

  const handleScroll = useCallback(() => {
    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      let active = "";

      for (const sectionId of sectionIds) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= offset && rect.bottom >= offset) {
            active = sectionId;
            break; // Exit early when found
          }
        }
      }

      if (active !== activeSection) {
        setActiveSection(active);
        onActiveChange(active);
      }
    });
  }, [sectionIds, activeSection, onActiveChange, offset]);

  useOptimizedScrollHandler(handleScroll, [handleScroll], { throttleMs });

  return activeSection;
};

// Performance monitoring utilities
export const performanceMonitor = {
  // Monitor scroll performance
  measureScrollPerformance: (callback: () => void) => {
    const start = performance.now();
    callback();
    const end = performance.now();

    if (end - start > 16) {
      // More than one frame (60fps)
      console.warn(`Slow scroll handler detected: ${end - start}ms`);
    }
  },

  // Monitor memory usage
  checkMemoryUsage: () => {
    if ("memory" in performance) {
      const memory = (performance as any).memory;
      const usedMB = memory.usedJSHeapSize / 1024 / 1024;
      const totalMB = memory.totalJSHeapSize / 1024 / 1024;

      if (usedMB > 100) {
        // More than 100MB
        console.warn(
          `High memory usage detected: ${usedMB.toFixed(2)}MB / ${totalMB.toFixed(2)}MB`,
        );
      }
    }
  },

  // Monitor frame rate
  monitorFrameRate: () => {
    let lastTime = performance.now();
    let frameCount = 0;

    const checkFrameRate = () => {
      const currentTime = performance.now();
      frameCount++;

      if (currentTime - lastTime >= 1000) {
        const fps = frameCount;
        frameCount = 0;
        lastTime = currentTime;

        if (fps < 30) {
          console.warn(`Low frame rate detected: ${fps}fps`);
        }
      }

      requestAnimationFrame(checkFrameRate);
    };

    requestAnimationFrame(checkFrameRate);
  },
};

// Scroll restoration utilities
export const scrollRestoration = {
  // Save scroll position
  saveScrollPosition: (key: string) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(`scroll-${key}`, window.scrollY.toString());
    }
  },

  // Restore scroll position
  restoreScrollPosition: (key: string) => {
    if (typeof window !== "undefined") {
      const savedPosition = sessionStorage.getItem(`scroll-${key}`);
      if (savedPosition) {
        window.scrollTo(0, parseInt(savedPosition, 10));
      }
    }
  },

  // Clear saved scroll position
  clearScrollPosition: (key: string) => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(`scroll-${key}`);
    }
  },
};
